// ============================================
// finalizeSessionRecording — Phase 3 of endSession
// ============================================
// Wave 5c — extracted from endSession() Phase 3.
//
// Two upload paths, in priority order:
//   1. Chunked finalization — if MediaRecorder uploaded chunks during the
//      call, flush remaining buffer, await all in-flight uploads, then call
//      /api/finalize-recording to merge the chunks into one file.
//   2. Single-blob fallback — if chunked upload wasn't active OR
//      finalization failed, do the original 3-step presigned upload:
//         a. POST /api/get-upload-url     (presigned PUT URL)
//         b. PUT  <presigned URL>         (direct to Supabase Storage)
//         c. POST /api/confirm-upload     (signed download URL)
//
// On success, mutates `recordingDataRef.current` with the finalized
// recording metadata (matches inline behavior). Returns the resolved
// audioUrl + audioStoragePath so the caller can attach them to the
// transcript payload.
//
// Caller responsibilities (kept inline):
//   - Short-call gating (skip when duration < 30s)
//   - Setting recordingDataRef defaults at session start
//   - Attaching audioUrl/audioStoragePath to the transcript POST body

import type { MutableRefObject } from 'react'
import {
  postConfirmUpload,
  postFinalizeRecording,
  postGetUploadUrl,
} from '../api/voice-bot-client'
import type { VoiceSessionData } from '../types'

export interface FinalizeSessionRecordingDeps {
  recordedAudioBlobRef: MutableRefObject<Blob | null>
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  chunkUploadActiveRef: MutableRefObject<boolean>
  chunkIndexRef: MutableRefObject<number>
  chunkBufferRef: MutableRefObject<Blob[]>
  chunkUploadPromisesRef: MutableRefObject<Promise<boolean>[]>
  recordingDataRef: MutableRefObject<{
    url: string | null
    storagePath: string | null
    size: number
    type: string
  }>
  flushChunkBuffer: () => Promise<boolean>
  publicKey?: string
  duration: number
}

export interface FinalizeSessionRecordingResult {
  audioUrl: string | null
  audioStoragePath: string | null
}

export const finalizeSessionRecording = async (
  deps: FinalizeSessionRecordingDeps,
): Promise<FinalizeSessionRecordingResult> => {
  const {
    recordedAudioBlobRef,
    sessionDataRef,
    chunkUploadActiveRef,
    chunkIndexRef,
    chunkBufferRef,
    chunkUploadPromisesRef,
    recordingDataRef,
    flushChunkBuffer,
    publicKey,
    duration,
  } = deps

  let audioUrl: string | null = null
  let audioStoragePath: string | null = null

  if (!recordedAudioBlobRef.current) {
    return { audioUrl, audioStoragePath }
  }

  // Stop accepting new chunks
  chunkUploadActiveRef.current = false

  const hasChunks =
    chunkIndexRef.current > 0 || chunkBufferRef.current.length > 0

  if (hasChunks) {
    // === CHUNKED FINALIZATION PATH ===
    try {
      console.log(
        `📦 Finalizing chunked recording (${chunkIndexRef.current} chunks uploaded so far)...`,
      )

      // Flush any remaining buffered chunks
      if (chunkBufferRef.current.length > 0) {
        console.log(
          `📦 Flushing final ${chunkBufferRef.current.length} buffered chunks...`,
        )
        const finalFlush = flushChunkBuffer()
        chunkUploadPromisesRef.current.push(finalFlush)
      }

      // Wait for all in-flight chunk uploads to complete
      const results = await Promise.allSettled(chunkUploadPromisesRef.current)
      const succeeded = results.filter(
        (r) => r.status === 'fulfilled' && r.value === true,
      ).length
      const failed = results.length - succeeded
      console.log(
        `📦 Chunk uploads: ${succeeded} succeeded, ${failed} failed out of ${results.length} total`,
      )

      // Call finalize-recording to merge chunks into single file
      console.log('🔧 Calling /api/finalize-recording...')
      const finalizeResponse = await postFinalizeRecording(
        {
          sessionId: sessionDataRef.current?.sessionId,
          customerId: sessionDataRef.current?.customerId,
          durationSeconds: duration,
        },
        publicKey,
      )

      if (finalizeResponse.ok) {
        const finalizeResult = await finalizeResponse.json()
        audioUrl = finalizeResult.audioUrl
        audioStoragePath = finalizeResult.filePath
        recordingDataRef.current = {
          url: audioUrl,
          storagePath: audioStoragePath,
          size: recordedAudioBlobRef.current.size,
          type: 'audio/webm',
        }
        console.log(`✅ Chunked recording finalized: ${audioStoragePath}`)
        console.log(`🔗 Signed URL expires: ${finalizeResult.expiresAt}`)
      } else {
        console.error('❌ Finalization failed:', finalizeResponse.status)
        console.warn('⚠️ Falling back to single-blob upload...')
      }
    } catch (finalizeErr) {
      console.error('❌ Chunked finalization error:', finalizeErr)
      console.warn('⚠️ Falling back to single-blob upload...')
    }
  }

  // === FALLBACK: SINGLE-BLOB UPLOAD (original approach) ===
  // Used when: chunked upload wasn't active, or finalization failed
  if (!audioUrl && recordedAudioBlobRef.current) {
    try {
      const audioBlob = recordedAudioBlobRef.current
      const audioSizeMB = (audioBlob.size / (1024 * 1024)).toFixed(2)
      console.log(
        `📤 Fallback: Uploading recording via single blob (${audioSizeMB} MB)...`,
      )

      // Step 1: Get presigned upload URL
      const urlResponse = await postGetUploadUrl(
        {
          sessionId: sessionDataRef.current?.sessionId,
          mimeType: audioBlob.type || 'audio/webm',
          fileSize: audioBlob.size,
        },
        publicKey,
      )

      if (!urlResponse.ok) {
        throw new Error(`Failed to get upload URL: ${urlResponse.status}`)
      }

      const { uploadUrl, filePath } = await urlResponse.json()

      // Step 2: Upload directly to Supabase Storage (bypasses Vercel)
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: audioBlob,
        headers: {
          'Content-Type': audioBlob.type || 'audio/webm',
          'x-upsert': 'false',
        },
      })

      if (!uploadResponse.ok) {
        throw new Error(`Direct upload failed: ${uploadResponse.status}`)
      }

      // Step 3: Confirm upload and get signed download URL
      const confirmResponse = await postConfirmUpload(
        {
          sessionId: sessionDataRef.current?.sessionId,
          filePath,
          fileSize: audioBlob.size,
          customerId: sessionDataRef.current?.customerId,
          durationSeconds: duration,
        },
        publicKey,
      )

      if (confirmResponse.ok) {
        const confirmResult = await confirmResponse.json()
        audioUrl = confirmResult.audioUrl
        audioStoragePath = confirmResult.filePath
        recordingDataRef.current = {
          url: audioUrl,
          storagePath: audioStoragePath,
          size: audioBlob.size,
          type: audioBlob.type || 'audio/webm',
        }
        console.log(`✅ Fallback upload successful: ${audioStoragePath}`)
      } else {
        console.error(
          '❌ Failed to confirm fallback upload:',
          confirmResponse.status,
        )
        console.warn('⚠️ Will send session data without recording')
      }
    } catch (uploadErr) {
      console.error('❌ Fallback upload failed:', uploadErr)
      console.warn('⚠️ Will send session data without recording')
    }
  }

  return { audioUrl, audioStoragePath }
}
